import { useMemo, useState } from 'react';
import { Alert, App, Button, Card, Checkbox, Modal, Progress, Segmented, Select, Space, Table, Tag, Tooltip, Typography } from 'antd';
import { EditOutlined, ReloadOutlined, ScanOutlined, SwapOutlined } from '@ant-design/icons';
import { addressCandidateKey, isEmptyAddress, normalizeAddress, type Address, type AddressCandidate, type AddressCandidateStatus, type AddressMergerApplyResult, type AddressTarget } from '@crm/shared';
import { PageHeader } from '../../components/PageHeader';
import { CardTitle } from '../../components/StatCard';
import { NameCell, TableSearch } from '../../components/NameCell';
import { EmptyState } from '../../components/states';
import { AddressFields, AddressParts } from '../../components/Addresses';
import { errorMessage } from '../../api/client';
import { useAddressMergerResults, useAddressMoveProgress, useApplyAddresses, useRecheckAddresses, useScanAddresses } from '../../features/cleanup/addresses-api';
import { fmtDateTime, plural } from '../../utils/format';
import { EntityTag } from '../../components/EntityTag';

interface Row extends AddressCandidate {
  rowKey: string;
  recordId: string;
  recordName: string;
  recordSub: string | null;
  saved: number;
}

type Tile = 'new' | 'duplicate' | 'leftover';

const STATUS: Record<AddressCandidateStatus, { label: string; color: string; description: string }> = {
  new: { label: 'New', color: 'processing', description: 'Not on the record yet: it will be added to its Addresses.' },
  duplicate: {
    label: 'Already saved',
    color: 'default',
    description: 'The record already has this address (or one with all of these parts): only its fields are cleared, and a missing label is filled in.',
  },
};

const TILES: { key: Tile; label: string; dot: string; description: string }[] = [
  { key: 'new', label: 'Addresses to add', dot: 'medium', description: STATUS.new.description },
  { key: 'duplicate', label: 'Already saved', dot: 'low', description: STATUS.duplicate.description },
  {
    key: 'leftover',
    label: 'With fields left in place',
    dot: 'high',
    description: 'Another field of the same address disagrees (for example two different cities). That field is left as it is: check it on the record afterwards.',
  },
];

const NOUN: Record<AddressTarget, [string, string]> = { contacts: ['contact', 'contacts'], organizations: ['organization', 'organizations'] };

export function AddressMergerPage() {
  const { message, modal } = App.useApp();
  const [target, setTarget] = useState<AddressTarget>('contacts');
  const stored = useAddressMergerResults(target);
  const scan = useScanAddresses();
  const recheck = useRecheckAddresses();
  const apply = useApplyAddresses();
  const [progress, setProgress] = useState<{ scanned: number; total: number; found: number } | null>(null);
  const moving = useAddressMoveProgress();
  const applied = moving.data;
  const [outcome, setOutcome] = useState<AddressMergerApplyResult | null>(null);
  const [tile, setTile] = useState<Tile | 'all'>('all');
  const [field, setField] = useState<string | undefined>();
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const [edits, setEdits] = useState<Record<string, Address>>({});
  const [editing, setEditing] = useState<{ key: string; value: Address } | null>(null);
  const [removeFromSource, setRemove] = useState(true);

  const data = stored.data;
  const [one, many] = NOUN[target];
  const base = target === 'contacts' ? '/contacts' : '/organizations';

  const allRows = useMemo<Row[]>(
    () =>
      (data?.results ?? []).flatMap((r) =>
        r.candidates.map((c) => ({ ...c, rowKey: addressCandidateKey(r.id, c.key), recordId: r.id, recordName: r.name, recordSub: r.subtitle, saved: r.addresses.length })),
      ),
    [data],
  );
  const inTile = (r: Row, t: Tile) => (t === 'leftover' ? r.leftover.length > 0 : r.status === t);

  const counts = useMemo(() => {
    const m: Record<Tile, number> = { new: 0, duplicate: 0, leftover: 0 };
    for (const r of allRows) for (const t of TILES) if (inTile(r, t.key)) m[t.key]++;
    return m;
  }, [allRows]);

  const fieldOptions = useMemo(() => {
    const m = new Map<string, { label: string; n: number }>();
    for (const r of allRows) for (const s of r.sources) m.set(s.field, { label: s.fieldLabel, n: (m.get(s.field)?.n ?? 0) + 1 });
    return [...m].map(([value, { label, n }]) => ({ value, label: `${label} (${n.toLocaleString()})` }));
  }, [allRows]);

  const rows = useMemo(() => {
    const needle = q.toLocaleLowerCase();
    return allRows
      .filter((r) => tile === 'all' || inTile(r, tile))
      .filter((r) => !field || r.sources.some((s) => s.field === field))
      .filter((r) => !needle || r.recordName.toLocaleLowerCase().includes(needle) || r.sources.some((s) => s.value.toLocaleLowerCase().includes(needle)));
  }, [allRows, tile, field, q]);

  const addressOf = (r: Row) => edits[r.rowKey] ?? r.address;

  const reset = () => {
    setSelected([]);
    setEdits({});
    setOutcome(null);
    setTile('all');
    setField(undefined);
  };

  const switchTarget = (t: AddressTarget) => {
    reset();
    setTarget(t);
  };

  const runScan = async () => {
    reset();
    setProgress({ scanned: 0, total: 0, found: 0 });
    try {
      stored.set(await scan.mutateAsync({ target, onProgress: setProgress }));
    } catch (e) {
      message.error(errorMessage(e));
    } finally {
      setProgress(null);
    }
  };

  const runMove = (list: Row[]) => {
    if (!list.length || !data) return;
    const adds = list.filter((r) => r.status === 'new').length;
    modal.confirm({
      title: `Move ${plural(list.length, 'address', 'addresses')}?`,
      content: (
        <div>
          {adds > 0 && <p>{plural(adds, 'address', 'addresses')} will be added to the {many}' Addresses as shown.</p>}
          {list.length > adds && <p>{plural(list.length - adds, 'address', 'addresses')} the {one} already has will only fill in a missing label.</p>}
          {removeFromSource ? (
            <p>The fields each address was made from are cleared. The change shows on each {one}'s timeline.</p>
          ) : (
            <p>The original fields are left as they are.</p>
          )}
        </div>
      ),
      okText: 'Move',
      // the modal closes at once; the move runs in the background with its progress at the top of the page
      onOk: () => void move(list),
    });
  };

  const move = async (list: Row[]) => {
    if (!data) return;
    const progressOf = (done: number) => moving.set({ target, done, total: list.length });
    progressOf(0);
    try {
      const result = await apply.mutateAsync({
        target,
        items: list.map((r) => ({ recordId: r.recordId, key: r.key, address: addressOf(r), sources: r.sources.map((s) => ({ field: s.field, value: s.value })) })),
        removeFromSource,
        onProgress: progressOf,
      });
      setOutcome(result);
      message.success(`Added ${plural(result.added, 'address', 'addresses')}${result.merged ? `, ${result.merged.toLocaleString()} already saved` : ''} on ${plural(result.records, one)}`);
      // records that are done now drop out of the results
      const ids = [...new Set(list.map((r) => r.recordId))];
      const moved = new Set(ids);
      const fresh = new Map((await recheck.mutateAsync({ target, ids })).map((c) => [c.id, c]));
      stored.set({ ...data, results: data.results.flatMap((c) => (moved.has(c.id) ? (fresh.has(c.id) ? [fresh.get(c.id)!] : []) : [c])) });
      setSelected([]);
    } catch (e) {
      message.error(errorMessage(e));
    } finally {
      moving.set(null);
    }
  };

  const saveEdit = () => {
    if (!editing) return;
    const value = normalizeAddress(editing.value);
    if (isEmptyAddress(value)) {
      message.error('An address needs at least one part');
      return;
    }
    setEdits((m) => ({ ...m, [editing.key]: value }));
    setEditing(null);
  };

  const selectedRows = rows.filter((r) => selected.includes(r.rowKey));
  const percent = progress?.total ? Math.min(99, Math.round((progress.scanned / progress.total) * 100)) : 0;

  return (
    <div className="page">
      <PageHeader
        title="Address Merger"
        subtitle="Turn address fields from imported lists (Street, City, Country… or one Address field) into each record's Addresses list."
        extra={
          <Space wrap>
            <Segmented
              value={target}
              onChange={(v) => switchTarget(v as AddressTarget)}
              disabled={!!progress || !!applied}
              options={[
                { value: 'contacts', label: 'Contacts' },
                { value: 'organizations', label: 'Organizations' },
              ]}
            />
            <Button type="primary" icon={data ? <ReloadOutlined /> : <ScanOutlined />} onClick={runScan} loading={scan.isPending} disabled={!!applied}>
              {data ? 'Scan again' : `Scan ${many}`}
            </Button>
          </Space>
        }
      />
      <Space orientation="vertical" size={24} style={{ width: '100%' }}>
        {applied && (
          <Card
            title={
              <CardTitle
                title={`Moving ${plural(applied.total, 'address', 'addresses')}`}
                subtitle={`${applied.done.toLocaleString()} of ${applied.total.toLocaleString()} done${applied.target !== target ? ` (${NOUN[applied.target][1]})` : ''} · it keeps going if you leave this page`}
              />
            }
          >
            <Progress percent={Math.min(99, Math.round((applied.done / applied.total) * 100))} status="active" />
          </Card>
        )}
        {progress ? (
          <Card title={<CardTitle title="Scanning" subtitle={`${progress.scanned.toLocaleString()} of ${plural(progress.total, one)} checked · ${plural(progress.found, one)} with addresses to move so far`} />}>
            <Progress percent={percent} status="active" />
          </Card>
        ) : !data ? (
          <Card title={<CardTitle title="How it works" subtitle="Nothing is changed by a scan." />}>
            <Typography.Paragraph>
              Every address is saved as <b>Address line 1</b>, <b>Address line 2</b>, <b>City</b>, <b>State / County / District</b>, <b>Country</b> and{' '}
              <b>ZIP / Postal code</b>, and a {one} can have several. The scan looks at the {one}'s custom fields named like an address part (Street, Address
              line 2, Town, County, Region, Zip, Postcode, Country…) and at fields holding a whole address (Address, Company address, Billing address…), which
              it splits into parts.
            </Typography.Paragraph>
            <Typography.Paragraph style={{ marginBottom: 0 }}>
              Fields with the same prefix make one address with that label (Billing city + Billing country → a <b>Billing</b> address). You then review each
              address, correct it if needed, and move it into the {one}'s Addresses, optionally clearing the fields it came from.
            </Typography.Paragraph>
          </Card>
        ) : (
          <>
            <Card
              title={
                <CardTitle
                  title={allRows.length ? `${plural(allRows.length, 'address', 'addresses')} found on ${plural(data.results.length, one)}` : 'Nothing to move'}
                  subtitle={`${plural(data.scanned, one)} checked ${fmtDateTime(data.finishedAt)}. Click a box to show only those addresses.`}
                />
              }
            >
              {allRows.length ? (
                <div className="issue-grid">
                  {TILES.filter((t) => counts[t.key]).map((t) => {
                    const active = tile === t.key;
                    return (
                      <Tooltip key={t.key} title={t.description}>
                        <button type="button" className={`issue-tile${active ? ' issue-tile-active' : ''}`} onClick={() => setTile(active ? 'all' : t.key)} aria-pressed={active}>
                          <span className={`issue-dot issue-dot-${t.dot}`} />
                          <span className="issue-tile-label">{t.label}</span>
                          <span className="issue-tile-count">{counts[t.key].toLocaleString()}</span>
                        </button>
                      </Tooltip>
                    );
                  })}
                </div>
              ) : (
                <Typography.Text type="secondary">No address fields with values on any {one}.</Typography.Text>
              )}
            </Card>

            {outcome && (outcome.skipped.length > 0 || outcome.merged > 0) && (
              <Alert
                type={outcome.skipped.length ? 'warning' : 'success'}
                showIcon
                closable
                onClose={() => setOutcome(null)}
                title={`Last move: ${plural(outcome.added, 'address', 'addresses')} added${outcome.merged ? `, ${outcome.merged.toLocaleString()} already saved` : ''}${outcome.skipped.length ? `, ${outcome.skipped.length.toLocaleString()} skipped` : ''}.`}
                description={
                  outcome.skipped.length > 0 && (
                    <ul style={{ margin: 0, paddingLeft: 18 }}>
                      {outcome.skipped.slice(0, 20).map((s) => (
                        <li key={`${s.recordId}${s.key}`}>
                          <EntityTag kind={base === '/organizations' ? 'organization' : 'contact'} id={s.recordId}>{data.results.find((r) => r.id === s.recordId)?.name ?? 'Record'}</EntityTag>: {s.reason}
                        </li>
                      ))}
                      {outcome.skipped.length > 20 && <li>and {plural(outcome.skipped.length - 20, 'more')}</li>}
                    </ul>
                  )
                }
              />
            )}

            {allRows.length > 0 && (
              <Card>
                <div className="table-toolbar">
                  <div className="toolbar-group">
                    <TableSearch value={q} onSearch={setQ} placeholder="Search name or field value" width={260} />
                    <Select allowClear placeholder="From any field" value={field} onChange={setField} options={fieldOptions} style={{ minWidth: 200 }} />
                    {tile !== 'all' && (
                      <Tag closable onClose={() => setTile('all')} color="processing">
                        {TILES.find((t) => t.key === tile)!.label}
                      </Tag>
                    )}
                  </div>
                  <div className="toolbar-group">
                    <Tooltip title="Clear the fields each address was made from. Untick to only copy the address into Addresses.">
                      <Checkbox checked={removeFromSource} onChange={(e) => setRemove(e.target.checked)}>
                        Clear original fields
                      </Checkbox>
                    </Tooltip>
                    <Button type="primary" icon={<SwapOutlined />} disabled={!!applied || !(selected.length ? selectedRows.length : rows.length)} loading={!!applied} onClick={() => runMove(selected.length ? selectedRows : rows)}>
                      {selected.length ? `Move ${selectedRows.length.toLocaleString()} selected` : `Move all ${rows.length.toLocaleString()} shown`}
                    </Button>
                  </div>
                </div>
                {applied && (
                  <div style={{ marginBottom: 16 }}>
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      Moving: {applied.done.toLocaleString()} of {plural(applied.total, 'address', 'addresses')} done
                    </Typography.Text>
                    <Progress percent={Math.min(99, Math.round((applied.done / applied.total) * 100))} status="active" />
                  </div>
                )}
                <Table<Row>
                  rowKey="rowKey"
                  dataSource={rows}
                  scroll={{ x: 1000 }}
                  rowSelection={{ selectedRowKeys: selected, onChange: (keys) => setSelected(keys as string[]), preserveSelectedRowKeys: true }}
                  pagination={{ defaultPageSize: 25, showSizeChanger: true, showTotal: (n) => plural(n, 'address', 'addresses') }}
                  locale={{ emptyText: <EmptyState description="No addresses match these filters." /> }}
                  columns={[
                    {
                      title: one.charAt(0).toUpperCase() + one.slice(1),
                      key: 'record',
                      width: 230,
                      render: (_, r) => (
                        <div>
                          <NameCell name={r.recordName} sub={r.recordSub ?? (target === 'contacts' ? 'No email' : 'No domain')} to={`${base}/${r.recordId}`} />
                          {r.saved > 0 && (
                            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                              {plural(r.saved, 'address', 'addresses')} saved
                            </Typography.Text>
                          )}
                        </div>
                      ),
                    },
                    {
                      title: 'From fields',
                      key: 'sources',
                      render: (_, r) => (
                        <div style={{ wordBreak: 'break-word' }}>
                          {r.sources.map((s) => (
                            <div key={s.field}>
                              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                {s.fieldLabel}:{' '}
                              </Typography.Text>
                              {s.value}
                            </div>
                          ))}
                          {r.leftover.map((s) => (
                            <Tooltip key={s.field} title="Disagrees with another field of this address, so it is left in its field">
                              <div style={{ color: '#B45309' }}>
                                <span style={{ fontSize: 12 }}>{s.fieldLabel} (left in place): </span>
                                {s.value}
                              </div>
                            </Tooltip>
                          ))}
                        </div>
                      ),
                    },
                    {
                      title: 'Address',
                      key: 'address',
                      width: 300,
                      render: (_, r) => (
                        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', justifyContent: 'space-between' }}>
                          <div style={{ minWidth: 0, wordBreak: 'break-word' }}>
                            <AddressParts address={addressOf(r)} />
                            {edits[r.rowKey] && (
                              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                Edited
                              </Typography.Text>
                            )}
                          </div>
                          <Tooltip title="Correct this address">
                            <Button type="text" size="small" aria-label={`Edit address for ${r.recordName}`} icon={<EditOutlined />} onClick={() => setEditing({ key: r.rowKey, value: addressOf(r) })} />
                          </Tooltip>
                        </div>
                      ),
                    },
                    {
                      title: 'Status',
                      key: 'status',
                      width: 130,
                      render: (_, r) => (
                        <Tooltip title={STATUS[r.status].description}>
                          <Tag color={STATUS[r.status].color}>{STATUS[r.status].label}</Tag>
                        </Tooltip>
                      ),
                    },
                  ]}
                />
              </Card>
            )}
          </>
        )}
      </Space>
      <Modal title="Edit address" open={!!editing} onCancel={() => setEditing(null)} onOk={saveEdit} okText="Use this address" destroyOnHidden>
        {editing && <AddressFields value={editing.value} onChange={(value) => setEditing({ ...editing, value })} />}
      </Modal>
    </div>
  );
}
